import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function OfficeLunchCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} Office Lunch Service`}
        type='website'
        canonical='/food-truck-catering/office-lunch'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Book recurring food ${vehicleType} lunch service for your workplace in ${city_name}. Office buildings, construction sites, hospitals, campuses & more.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Office Lunch Service in ${city_name}`,
            description: `Book recurring food ${vehicleType} lunch service for your workplace in ${city_name}. Office buildings, construction sites, hospitals, campuses and more.`,
            url: '/food-truck-catering/office-lunch',
            serviceType: 'Food Truck Office Lunch Service',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Office Lunch', item: '/food-truck-catering/office-lunch' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Recurring Food {vehicle_type} Service for Your Workplace</h1>
            <p>Give your employees, crew, or staff something to look forward to every week. {site_title} makes it easy to schedule recurring food {vehicleType} visits to your office building, construction site, hospital, or campus in {city_name}. Fresh, made-to-order meals keep your team fueled, happy, and productive — no cafeteria or kitchen required.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Schedule Recurring Lunch Service</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Workplace Lunch`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Food ${vehicle_type} for Your Workplace?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Food {vehicle_type} Lunch for Every Workplace</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Office Building Lunch Service</h3>
            <p>Break up the midday routine with a rotating lineup of food {vehicleType}s at your office. Employees love the variety — tacos one week, poke bowls the next, followed by gourmet burgers or wood-fired pizza. Recurring {vehicleType} visits boost morale, encourage team bonding over lunch, and give your workplace a perk that stands out.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Construction Site & Industrial Catering</h3>
            <p>Construction crews and industrial workers need hearty, satisfying meals to power through the day. Food {vehicleType}s deliver hot, fresh food right to the job site — no break room or nearby restaurants needed. From loaded burritos and BBQ plates to classic burgers and fries, {city_name}&#39;s food {vehicleType}s know how to feed a hardworking crew quickly and affordably.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Hospitals, Campuses & Weekly Routes</h3>
            <p>Hospital staff, university students, and campus employees all benefit from convenient, on-site food {vehicleType} service. Whether it&#39;s a weekly visit or a daily lunch rotation, food {vehicleType}s bring diverse, high-quality meals to locations where dining options may be limited. {site_title} can help you set up a regular schedule with {city_name}&#39;s most reliable {vehicleType}s, so your people always have something great to eat.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Book a Food ${vehicle_type} for Your Workplace!`} />
      </section>
    </div>
  );
}

export default OfficeLunchCatering;
